import { useEffect } from 'react';
import useStore from '../store/useStore';
import { getBackendRewardAmount, getProductPromoPercent } from '../utils/productUtils';
import { getRemainingToTodayEndMs } from '../utils/serverClock';
import ProductCard from './ProductCard';
import PromoBanner from './PromoBanner';

const CROSS_DAY_REFRESH_DELAY_MS = 1000;

/**
 * 商品列表组件
 *
 * 根据需求文档显示规则：
 * - 金币页签：1个基准商品(9.99) + 8个促销商品(9.99-399.99) = 共9个商品，按价格从高到低显示
 * - 钻石页签：1个基准商品(9.99) + 4个促销商品(9.99-99.99) = 共5个商品，按价格从高到低显示
 */

const ProductList = () => {
  const {
    products,
    coinProducts,
    baseCoinProducts,
    gemProducts,
    baseGemProducts,
    coinSaleActivity,
    gemSaleActivity,
    productsLoaded,
    activitiesLoaded,
    fetchProductList,
    fetchSaleActivities,
    isAuthenticated,
    user,
    authCheckDone
  } = useStore();

  // 自动登录校验完成后，再获取商品列表，避免刷新时先用旧 token 拉取
  useEffect(() => {
    if (!authCheckDone) return;
    if (isAuthenticated && !productsLoaded) {
      fetchProductList();
    }
  }, [authCheckDone, isAuthenticated, productsLoaded, fetchProductList]);

  // 自动登录校验完成后，再获取促销活动（独立于商品加载状态）
  // 只在未加载时触发，具体防重由 store 内部 loading 锁兜底
  useEffect(() => {
    if (!authCheckDone) return;
    if (isAuthenticated && !activitiesLoaded) {
      fetchSaleActivities();
    }
  }, [authCheckDone, isAuthenticated, activitiesLoaded, fetchSaleActivities]);

  // 跨天刷新：按后端返回的 EndOfDay 触发（和老虎机一致）
  useEffect(() => {
    if (!isAuthenticated) return;
    const remainingMs = getRemainingToTodayEndMs(user);
    if (remainingMs === null) {
      console.warn('[countdown-debug][ProductList] missing backend cross-day time', {
        todayEnd: user?.todayEnd,
        today_end: user?.today_end,
        serverNow: user?.serverNow,
        serverTimeOffsetMs: user?.serverTimeOffsetMs,
      });
      return;
    }



    const midnightTimer = setTimeout(() => {
      // 处理完之后跨天时间++（防止下一轮计时丢失）
      const state = useStore.getState();
      const currentUser = state.user;
      const currentTodayEnd = Number(currentUser?.todayEnd ?? currentUser?.today_end);

      if (currentUser && Number.isFinite(currentTodayEnd) && currentTodayEnd > 0) {
        const nextTodayEnd = currentTodayEnd + 86400;
        state.setUser(
          {
            ...currentUser,
            todayEnd: nextTodayEnd,
            today_end: nextTodayEnd,
          },
          { persist: currentUser.provider !== 'game' }
        );
      }

      // 重置商品加载状态，触发重新获取
      useStore.getState().resetProductsLoaded();
      // 同步重置活动缓存，触发重新获取活动
      useStore.getState().resetActivitiesLoaded();
    }, remainingMs + CROSS_DAY_REFRESH_DELAY_MS);

    return () => clearTimeout(midnightTimer);
  }, [isAuthenticated, user?.todayEnd, user?.today_end, user?.serverNow, user?.serverTimeOffsetMs]);

  // 渲染商品区域（带标题）
  const renderProductSection = (title, icon, products, type) => {
    if (!products || products.length === 0) return null;

    return (
      <section className="mt-6 sm:mt-8">
        <div className="mb-4 flex items-center gap-3 sm:mb-5">
          <div className={`flex h-10 w-10 items-center justify-center rounded-xl border text-xl shadow-[0_14px_28px_rgba(86,104,255,0.08)] sm:h-12 sm:w-12 sm:rounded-2xl sm:text-2xl ${
            type === 'coin'
              ? 'border-[#ffe4b1] bg-[#fff6df] text-[#8b670b] dark:border-gold/20 dark:bg-gold/10 dark:text-gold'
              : 'border-[#cde9ff] bg-[#edf8ff] text-[#2e74c6] dark:border-sapphire/20 dark:bg-sapphire/10 dark:text-sapphire'
          }`}>
            {icon}
          </div>
          <div>
            <h3 className="font-display text-2xl font-black uppercase text-[#251b46] dark:text-white sm:text-3xl">
              {title}
            </h3>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-3.5 sm:grid-cols-2 sm:gap-4 xl:grid-cols-3">
          {products.map((product) => (
            <ProductCard key={product.id} product={product} type={type} />
          ))}
        </div>
      </section>
    );
  };

  // 需求指定的价格列表
  const COIN_PROMO_PRICES = [9.99, 19.99, 49.99, 99.99, 149.99, 199.99, 299.99, 399.99];
  const GEM_PROMO_PRICES = [9.99, 19.99, 49.99, 99.99];

  // 辅助函数：检查价格是否匹配（处理浮点数精度问题）
  const isPriceMatch = (price, target) => Math.abs(price - target) < 0.01;

  // 过滤并排序金币商品
  // 基准商品：只保留 9.99 USD，取1个
  const filteredBaseCoin = baseCoinProducts
    .filter(p => isPriceMatch(p.price, 9.99))
    .slice(0, 1);
  // 促销商品：只保留指定价格，最多8个
  const filteredCoinPromo = coinProducts
    .filter(p => COIN_PROMO_PRICES.some(target => isPriceMatch(p.price, target)))
    .sort((a, b) => b.price - a.price)
    .slice(0, 8);

  // 过滤并排序钻石商品
  // 基准商品：只保留 9.99 USD，取1个
  const filteredBaseGem = baseGemProducts
    .filter(p => isPriceMatch(p.price, 9.99))
    .slice(0, 1);
  // 促销商品：只保留指定价格，最多4个
  const filteredGemPromo = gemProducts
    .filter(p => GEM_PROMO_PRICES.some(target => isPriceMatch(p.price, target)))
    .sort((a, b) => b.price - a.price)
    .slice(0, 4);
  const hasGemRealPromo = filteredGemPromo.some((p) => getProductPromoPercent(p) > 0);

  // 实际渲染到页面的商品列表
  // 规则：
  // 1) 基准商品固定放在最后
  // 2) 钻石如果没有真实促销（无加成），不显示基准商品
  const renderedCoinProducts = [...filteredCoinPromo, ...filteredBaseCoin];
  const renderedGemProducts = hasGemRealPromo
    ? [...filteredGemPromo, ...filteredBaseGem]
    : filteredGemPromo;
  const coinProductMaxBonus = renderedCoinProducts.reduce((maxValue, product) => {
    return Math.max(maxValue, getProductPromoPercent(product));
  }, 0);
  const gemProductMaxBonus = renderedGemProducts.reduce((maxValue, product) => {
    return Math.max(maxValue, getProductPromoPercent(product));
  }, 0);
  const coinMaxBonus = coinProductMaxBonus;
  const gemMaxBonus = gemProductMaxBonus;

  useEffect(() => {
    if (!isAuthenticated || !productsLoaded) return;

    const formatDisplayProductSummary = (section, product) => ({
      section,
      productId: product?.id,
      baseValue: product?.baseValue,
      rewardFromRewards: getBackendRewardAmount(product),
    });

   
  }, [isAuthenticated, productsLoaded, renderedCoinProducts, renderedGemProducts]);

  // 轮播活动开启判断（用户规则）：
  // 只按“活动是否开启”决定轮播显示，不再用商品加成作为兜底条件。
  // 这样可确保：你没开活动时绝不会显示轮播，开了活动才显示轮播。
  const hasCoinSaleActivity = !!coinSaleActivity;
  const hasGemSaleActivity = !!gemSaleActivity;

  return (
    <>
      {/* 顶部 Banner 显示规则（按用户最新要求）：
          1) 无活动：显示默认“BEST VALUE EVERY DAY”样式；
          2) 有活动：切换为活动轮播内容。
          因此这里始终挂载 PromoBanner，由组件内部根据活动开关决定展示内容。 */}
      <PromoBanner
        coinMaxBonus={coinMaxBonus}
        gemMaxBonus={gemMaxBonus}
        coinPromoActive={hasCoinSaleActivity}
        gemPromoActive={hasGemSaleActivity}
      />

      {/* Coins 页签：1个基准 + 8个促销 = 9个商品 */}
      {renderProductSection(
        'Coins',
        <img src="/assets/coin/icon_coin_2.webp" alt="Coins icon" className="h-8 w-8 object-contain" />,
        renderedCoinProducts,
        'coin'
      )}

      {/* Gems 页签：1个基准 + 4个促销 = 5个商品 */}
      {renderProductSection('Gems', '💎', renderedGemProducts, 'gem')}

      {/* 如果没有商品数据，显示加载状态 */}
      {!productsLoaded && isAuthenticated && (
        <div className="mt-8 flex items-center justify-center py-16">
          <div className="text-center">
            <div className="mx-auto h-12 w-12 animate-spin rounded-full border-4 border-gold/20 border-t-gold"></div>
            <p className="mt-4 text-textSecondary">Loading products...</p>
          </div>
        </div>
      )}

      {/* 如果所有分类都为空且已加载 */}
      {productsLoaded && products.length === 0 && (
        <div className="mt-8 text-center py-16">
          <p className="text-textSecondary">No products available</p>
        </div>
      )}
    </>
  );
};

export default ProductList;
